import { z } from 'zod';
import { CANONICAL_UNITS } from '../units.js';
import { clientRequestIdSchema, instantSchema, localDateSchema, objectIdSchema, timezoneSchema, } from './common.js';
import { absoluteNutrientsSchema, nutritionBasisSchema, nutrientsSchema } from './nutrients.js';
import { goalsSchema } from './auth.js';
import { servingSchema } from './food.js';
export const mealTypeSchema = z.enum(['breakfast', 'lunch', 'dinner', 'snack']);
export const MEAL_TYPES = mealTypeSchema.options;
export const quantitySchema = z.object({
    amount: z.number().positive().max(100000),
    unit: z.enum(CANONICAL_UNITS),
});
/**
 * A food to log, given either by reference to the catalog or inline.
 *
 * Inline is what the label scanner and the free-text parser produce: nutrition
 * facts with no catalog entry behind them yet.
 */
export const foodRefSchema = z.union([
    z.object({ foodId: objectIdSchema }),
    z.object({
        name: z.string().trim().min(1).max(200),
        brand: z.string().trim().max(120).nullable().optional(),
        basis: nutritionBasisSchema.default('per_100g'),
        nutrients: nutrientsSchema,
        serving: servingSchema.nullable().optional(),
        density: z.number().min(0.1).max(3).nullable().optional(),
        /** Persist this inline food into the user's own catalog while logging. */
        saveToCatalog: z.boolean().default(false),
        /**
         * Barcode to attach when saving to the catalog. This is what closes the
         * barcode -> label loop: a product OpenFoodFacts did not know, scanned from
         * its label, becomes locally resolvable by that barcode next time. Only
         * honoured together with `saveToCatalog`.
         */
        barcode: z
            .string()
            .trim()
            .regex(/^\d{8}$|^\d{12,14}$/)
            .nullable()
            .optional(),
    }),
]);
export const createEntrySchema = z.object({
    food: foodRefSchema,
    quantity: quantitySchema,
    mealType: mealTypeSchema.default('snack'),
    loggedAt: instantSchema.optional(),
    note: z.string().trim().max(280).optional(),
});
/** Logging a parsed sentence or a scanned label writes several entries at once. */
export const createEntriesSchema = z.object({
    entries: z.array(createEntrySchema).min(1).max(50),
    /** Present when the write came from the offline queue. See the schema's docs. */
    clientRequestId: clientRequestIdSchema.optional(),
});
export const updateEntrySchema = z
    .object({
    quantity: quantitySchema.optional(),
    mealType: mealTypeSchema.optional(),
    loggedAt: instantSchema.optional(),
    note: z.string().trim().max(280).nullable().optional(),
})
    .refine((value) => Object.keys(value).length > 0, 'no fields to update');
export const entrySchema = z.object({
    id: objectIdSchema,
    foodId: objectIdSchema.nullable(),
    name: z.string(),
    brand: z.string().nullable(),
    mealType: mealTypeSchema,
    loggedAt: z.string(),
    quantity: quantitySchema,
    /** Grams (or ml) the quantity resolved to. */
    resolvedGrams: z.number(),
    basis: nutritionBasisSchema,
    /** Snapshot of the food's per-100 values at the moment of logging. */
    per100: nutrientsSchema,
    /** What was actually consumed: `per100` scaled by `resolvedGrams`. */
    totals: absoluteNutrientsSchema,
    note: z.string().nullable(),
    createdAt: z.string(),
});
export const diaryQuerySchema = z.object({
    date: localDateSchema.optional(),
    timezone: timezoneSchema.optional(),
});
export const mealGroupSchema = z.object({
    mealType: mealTypeSchema,
    entries: z.array(entrySchema),
    totals: absoluteNutrientsSchema,
});
export const daySummarySchema = z.object({
    date: localDateSchema,
    timezone: z.string(),
    totals: absoluteNutrientsSchema,
    goals: goalsSchema,
    /** goals minus totals; null for any goal the user has not set. */
    remaining: z.object({
        kcal: z.number().nullable(),
        protein: z.number().nullable(),
        carbs: z.number().nullable(),
        fat: z.number().nullable(),
    }),
    meals: z.array(mealGroupSchema),
    entryCount: z.number().int(),
});
export const rangeQuerySchema = z.object({
    from: localDateSchema,
    to: localDateSchema,
    timezone: timezoneSchema.optional(),
});
// ---------------------------------------------------------------------------
// Recently logged
// ---------------------------------------------------------------------------
export const recentQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(31).default(7),
    timezone: timezoneSchema.optional(),
});
/**
 * A food logged recently, collapsed to one row per food per meal.
 *
 * Carries enough to re-log it in a single tap: the nutrient snapshot, the
 * amount used last time, and — where the amount was countable — the serving
 * weight recovered from that entry's own arithmetic, since an ad-hoc entry has
 * no catalog food to look it up from.
 */
export const recentItemSchema = z.object({
    foodId: objectIdSchema.nullable(),
    name: z.string(),
    brand: z.string().nullable(),
    basis: nutritionBasisSchema,
    per100: nutrientsSchema,
    /** The amount used the last time this was logged. */
    quantity: quantitySchema,
    /** Weight of one piece/portion, when the last amount was countable. */
    servingGrams: z.number().nullable(),
    lastLoggedAt: z.string(),
    timesLogged: z.number().int(),
});
export const recentMealSchema = z.object({
    mealType: mealTypeSchema,
    items: z.array(recentItemSchema),
});
export const recentResultSchema = z.object({
    /** Always all four meals, in order, so the UI layout is stable. */
    meals: z.array(recentMealSchema),
    days: z.number().int(),
});
// ---------------------------------------------------------------------------
// Copying entries
// ---------------------------------------------------------------------------
/**
 * Re-logs existing entries, faithfully. The source entries are identified by
 * id and cloned from their stored snapshots, so a copy carries exactly the
 * nutrients that were recorded — no re-resolution against a catalog food that
 * may since have changed.
 */
export const copyEntriesSchema = z.object({
    entryIds: z.array(objectIdSchema).min(1).max(100),
    /** Target day; defaults to today in the user's timezone. */
    date: localDateSchema.optional(),
    timezone: timezoneSchema.optional(),
    /** Override the meal on the copies; otherwise each keeps its original. */
    mealType: mealTypeSchema.optional(),
});
// ---------------------------------------------------------------------------
// Logging consistency
// ---------------------------------------------------------------------------
export const diaryStatsQuerySchema = z.object({
    days: z.coerce.number().int().min(7).max(400).default(90),
    timezone: timezoneSchema.optional(),
});
/**
 * How consistently the diary is kept.
 *
 * Worth surfacing because every average the app reports is computed over
 * *logged* days: a 2 000 kcal average across nine of thirty days says much less
 * than the same number across all thirty, and only this makes that visible.
 */
export const diaryStatsSchema = z.object({
    /** Days in the examined window. */
    windowDays: z.number().int(),
    /** Days within it carrying at least one entry. */
    loggedDays: z.number().int(),
    /**
     * Consecutive logged days up to and including today. A day that is merely
     * still in progress does not break it: if today has nothing yet but yesterday
     * does, the streak stands.
     */
    currentStreak: z.number().int(),
    /** Longest run of consecutive logged days in the window. */
    longestStreak: z.number().int(),
    /** Whether today already has an entry — drives "keep it going" prompts. */
    loggedToday: z.boolean(),
});
export const dayTotalsSchema = z.object({
    date: localDateSchema,
    totals: absoluteNutrientsSchema,
    entryCount: z.number().int(),
});
//# sourceMappingURL=diary.js.map